import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/UserAvatar";
import UsageDrillDown from "@/components/UsageDrillDown";
import {
  ChartLine, Timer, Users, TrendUp, FilePdf, FileCsv, Trophy, Clock, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: "today", label: "Hoy" },
  { value: "7d", label: "7 días" },
  { value: "30d", label: "30 días" },
  { value: "90d", label: "90 días" },
  { value: "all", label: "Todo" },
];

const HUMAN_PAGES = {
  dashboard: "Inicio",
  clubes: "Clubes",
  torneos: "Torneos",
  ligas: "Ligas",
  retas: "Retas",
  marcador: "Marcador",
  comunidad: "Comunidad",
  comic: "Cómic",
  "dyd-run": "DYD Run",
  grupos: "Grupos",
  rentas: "Rentas de cancha",
  streaming: "Streaming",
  estadisticas: "Estadísticas",
  chat: "Chat",
  tienda: "Tienda",
  perfil: "Perfil",
  "mi-perfil": "Mi Perfil",
  "uso-plataforma": "Uso Plataforma",
};

function fmtDuration(secs) {
  const s = Math.max(0, parseInt(secs || 0, 10));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

function humanPage(p) { return HUMAN_PAGES[p] || p || "otro"; }

export default function PlatformUsage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [pages, setPages] = useState([]);
  const [users, setUsers] = useState([]);
  const [heatmap, setHeatmap] = useState({ grid: [], days: [] });
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [drillUser, setDrillUser] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const roleQuery = roleFilter !== "all" ? `&role=${roleFilter}` : "";
      const [o, t, p, u, h] = await Promise.all([
        api.get(`/admin/usage/overview?period=${period}`),
        api.get(`/admin/usage/timeline?period=${period}`),
        api.get(`/admin/usage/pages?period=${period}&limit=15`),
        api.get(`/admin/usage/users?period=${period}&limit=100${roleQuery}`),
        api.get(`/admin/usage/heatmap?period=${period}`),
      ]);
      setOverview(o.data);
      setTimeline(t.data);
      setPages(p.data);
      setUsers(u.data);
      setHeatmap(h.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar métricas");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period, roleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runBackfill = async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/usage/backfill");
      toast.success(data.skipped ? "Backfill ya ejecutado previamente" : `Backfill: ${data.inserted} eventos insertados`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setRefreshing(false); }
  };

  const download = async (kind) => {
    const url = `${BACKEND_URL}/api/admin/usage/export.${kind}?period=${period}`;
    const token = localStorage.getItem("padel_token");
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `uso_plataforma_${period}.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("No se pudo descargar el archivo"); }
  };

  const heatMax = useMemo(() => {
    if (!heatmap.grid?.length) return 1;
    let mx = 1;
    for (const row of heatmap.grid) for (const v of row) if (v > mx) mx = v;
    return mx;
  }, [heatmap]);

  return (
    <div className="space-y-6 pb-8" data-testid="platform-usage-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1.5">
            <ChartLine size={12} weight="fill" /> Admin · Analytics
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-1">
            Uso de plataforma
          </h1>
          <p className="text-xs text-gray-400 mt-1">Quién usa la app, cuánto tiempo y en qué apartados. Métricas en tiempo real + backfill histórico.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-black/60 border-white/15 h-9 text-xs" data-testid="usage-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-36 bg-black/60 border-white/15 h-9 text-xs" data-testid="usage-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="directivo">Directivo</SelectItem>
              <SelectItem value="profesor">Profesor</SelectItem>
              <SelectItem value="jugador">Jugador</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => download("csv")} className="h-9 border-white/15 text-gray-300 hover:text-[#CCFF00] text-[10px] uppercase" data-testid="usage-export-csv">
            <FileCsv size={14} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} className="h-9 border-white/15 text-gray-300 hover:text-[#CCFF00] text-[10px] uppercase" data-testid="usage-export-pdf">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={runBackfill} disabled={refreshing} className="h-9 border-[#CCFF00]/40 text-[#CCFF00] text-[10px] uppercase" data-testid="usage-backfill-btn">
              <ArrowsClockwise size={14} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} /> Backfill
            </Button>
          )}
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="usage-kpis">
        <Kpi icon={Users} color="#CCFF00" label="DAU" value={overview?.dau ?? "—"} sub="hoy" />
        <Kpi icon={Users} color="#4AA6FF" label="WAU" value={overview?.wau ?? "—"} sub="7 días" />
        <Kpi icon={Users} color="#FFD24A" label="MAU" value={overview?.mau ?? "—"} sub="30 días" />
        <Kpi icon={Clock} color="#FF6B4A" label="Tiempo total" value={overview ? fmtDuration(overview.total_seconds) : "—"} sub={`${overview?.sessions ?? 0} sesiones`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Kpi icon={Timer} color="#CCFF00" label="Duración media sesión" value={overview ? fmtDuration(overview.avg_session_seconds) : "—"} sub="" wide />
        <Kpi icon={TrendUp} color="#4AA6FF" label="Tiempo medio por usuario" value={overview ? fmtDuration(overview.avg_seconds_per_user) : "—"} sub={`${overview?.users_count ?? 0} usuarios`} wide />
      </div>

      {/* Timeline */}
      <section className="card-tactical p-4 sm:p-6" data-testid="usage-timeline-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Actividad por día</h2>
          <Badge className="bg-black/60 text-[10px] uppercase border-white/10">{timeline.length} días con actividad</Badge>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeline.map((t) => ({ ...t, minutes: Math.round(t.seconds / 60) }))} margin={{ top: 5, right: 15, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => (v || "").slice(5)} />
              <YAxis yAxisId="l" stroke="#CCFF00" fontSize={10} />
              <YAxis yAxisId="r" orientation="right" stroke="#4AA6FF" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 4 }} />
              <Line yAxisId="l" type="monotone" dataKey="minutes" stroke="#CCFF00" strokeWidth={2} name="Minutos" dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="users" stroke="#4AA6FF" strokeWidth={2} name="Usuarios únicos" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Heatmap */}
      <section className="card-tactical p-4 sm:p-6" data-testid="usage-heatmap-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Horas activas · Heatmap</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">Día × Hora</span>
        </div>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="grid" style={{ gridTemplateColumns: `36px repeat(24, 20px)`, gap: 2 }}>
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="text-[9px] text-gray-500 text-center">{h}</div>
              ))}
              {heatmap.grid.map((row, di) => (
                <div key={di} className="contents">
                  <div className="text-[10px] text-gray-400 uppercase pr-1 flex items-center justify-end">{heatmap.days?.[di] ?? di}</div>
                  {row.map((v, hi) => {
                    const intensity = heatMax > 0 ? Math.min(1, v / heatMax) : 0;
                    const alpha = 0.1 + intensity * 0.9;
                    return (
                      <div
                        key={hi}
                        className="w-5 h-5 rounded-sm hover:ring-1 hover:ring-[#CCFF00] cursor-help"
                        style={{ background: `rgba(204, 255, 0, ${alpha})` }}
                        title={`${heatmap.days?.[di]} ${hi}:00 — ${fmtDuration(v)}`}
                        data-testid={`heat-${di}-${hi}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Top pages */}
      <section className="card-tactical p-4 sm:p-6" data-testid="usage-pages-card">
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-3">Apartados con más tiempo</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pages.map((p) => ({ ...p, minutes: Math.round(p.seconds / 60), pageLabel: humanPage(p.page) }))} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="pageLabel" stroke="#666" fontSize={9} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis stroke="#CCFF00" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333" }} />
              <Bar dataKey="minutes" fill="#CCFF00" radius={[2, 2, 0, 0]} name="Minutos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Users ranking */}
      <section className="card-tactical p-4 sm:p-6" data-testid="usage-users-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight flex items-center gap-2"><Trophy size={20} className="text-[#CCFF00]" /> Ranking de usuarios</h2>
          <Badge className="bg-black/60 text-[10px] uppercase border-white/10">{users.length} usuarios</Badge>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-6">Sin datos para este periodo.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="usage-users-list">
            {users.map((u, i) => (
              <li key={u.user_id}>
                <button onClick={() => setDrillUser(u)} className="w-full flex items-center gap-3 p-2 bg-black/40 border border-white/10 rounded hover:border-[#CCFF00]/60 transition-colors" data-testid={`usage-user-${u.user_id}`}>
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs shrink-0 ${i < 3 ? "bg-[#CCFF00] text-black" : "bg-white/10 text-gray-300"}`}>
                    {i + 1}
                  </span>
                  <UserAvatar src={u.photo} fallback={(u.name || "?").slice(0, 2)} className="w-9 h-9 rounded-full border border-white/10" />
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-bold text-white truncate">{u.name || "Usuario"}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">{u.role} · {u.days_active}d activos · {u.pages_visited} pantallas</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-black text-[#CCFF00] font-['Barlow_Condensed']">{fmtDuration(u.seconds)}</p>
                    <p className="text-[10px] text-gray-500">{u.sessions} sesiones</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <UsageDrillDown user={drillUser} period={period} onClose={() => setDrillUser(null)} />
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub, wide }) {
  return (
    <div className={`card-tactical p-3 sm:p-4 relative overflow-hidden ${wide ? "col-span-1" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} />
      <div className="flex items-center justify-between pl-2">
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
          <p className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black leading-none mt-1" style={{ color }}>{value}</p>
          {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <Icon size={24} weight="fill" style={{ color, opacity: 0.5 }} />
      </div>
    </div>
  );
}
